// TipTapEditor.js
import React, { useImperativeHandle, forwardRef } from 'react';
import { Color } from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Placeholder from '@tiptap/extension-placeholder'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBold,
  faItalic,
  faStrikethrough,
  faCode,
  faListUl,
  faListOl,
  faUndo,
  faRedo,
  faQuoteRight,
  faMinus,
  faBroom,
} from '@fortawesome/free-solid-svg-icons'


const TiptapEditor = forwardRef(({ content, name = "html" }, ref) => {

  const CustomTextStyle = TextStyle.extend({
    addAttributes() {
      return {
        fontSize: {
          default: null,
          parseHTML: element => ({
            fontSize: element.style.fontSize || null,
          }),
          renderHTML: attributes => {
            if (!attributes.fontSize) return {}
            return {
              style: `font-size: ${attributes.fontSize}`,
            }
          },
        },
      }
    },
  })

  const MenuBar = () => {
    const handleColorChange = (e) => {
      e.preventDefault();
      editor.chain().focus().setColor(e.target.value).run()
    }
  
    const handleFontSizeChange = (e) => {
      e.preventDefault();
      const size = e.target.value
      editor.chain().focus().setMark('textStyle', { fontSize: size }).run()
    }
  
    return (
      <div className="control-group" role="toolbar" aria-label="Text formatting toolbar">
        <div className="button-group">
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}>
            <FontAwesomeIcon icon={faBold} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}>
            <FontAwesomeIcon icon={faItalic} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()}>
            <FontAwesomeIcon icon={faStrikethrough} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleCode().run()}>
            <FontAwesomeIcon icon={faCode} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <FontAwesomeIcon icon={faListUl} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <FontAwesomeIcon icon={faListOl} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <FontAwesomeIcon icon={faQuoteRight} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <FontAwesomeIcon icon={faMinus} />
          </button>
          <select
            onChange={(e) => {
              e.preventDefault();
              const level = parseInt(e.target.value);
              if (level === 0) {
                editor.chain().focus().setParagraph().run();
              } else {
                editor.chain().focus().toggleHeading({ level }).run();
              }
            }}
            value={editor.isActive("paragraph") ? "0" :
                  [1, 2, 3, 4, 5, 6].find((lvl) => editor.isActive("heading", { level: lvl })) || ""}
          >
            <option value="0">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="5">Heading 5</option>
            <option value="6">Heading 6</option>
          </select>
          <button type="button" onClick={() => editor.chain().focus().undo().run()}>
            <FontAwesomeIcon icon={faUndo} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()}>
            <FontAwesomeIcon icon={faRedo} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().clearNodes().run()}>
            <FontAwesomeIcon icon={faBroom} />
          </button>
          <div className="color-picker" >
            <input
              type="color"
              onChange={handleColorChange}
              title="Text color"
            />
          </div>
          <select
            onChange={handleFontSizeChange}
            defaultValue=""
          >
            <option value="" disabled>Size</option>
            <option value="10pt">10</option>
            <option value="11pt">11</option>
            <option value="12pt">12</option>
            <option value="14pt">14</option>
            <option value="16pt">16</option>
            <option value="18pt">18</option>
            <option value="20pt">20</option>
          </select>
        </div>
      </div>
    )
  }
  
  const extensions = [
    Color,
    CustomTextStyle,
    TextStyle,
    StarterKit,
    Placeholder.configure({
      placeholder: 'Type your message here...'
    }),
  ]

  const editor = useEditor({
    content,
    extensions: extensions,
    editorProps: {
      attributes: {
        id: 'EditorContent',
      }
    },
  });

  useImperativeHandle(ref, () => {
    return {
      getHTML: () => {
        if (!editor) {
          console.warn("Editor not ready yet");
          return '';
        }
        return editor.getHTML();
      },
    };
  }, [editor]);

  if (!editor) return null;

  return (
    <div id="editor" role="HTML Editor" className="instavite-editor" >
        <MenuBar editor={editor} />
        <EditorContent name={name} value={editor.current?.getHTML() || ''} editor={editor} />
    </div>
  );
});

export default TiptapEditor;
